import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { getAuthHeader } from '#/api/auth';
import { config } from '#/config';
import { sendPrompt } from '#/api/prompt';
import { getFeature, sendFeatureSelections } from '#/api/feature';
import { Feature } from '#/types/message';
import { FeatureSelection } from '../feature-selection/feature-selection';
import { updateSettings } from '#/api/settings';
import LoadingSpinner from '../loading-spinner/loading-spinner';
import NoAccess, { NoAccessProps } from '../no-access/no-access';

// Import components
import Messages from '../messages/messages';
import PromptContainer from '../prompt/prompt';
import Logo from '../logo/logo';
import TopBarCenter from '../top-bar-center/top-bar-center';
import Settings from '../settings/settings';
import SessionInitModal from '../session-init-modal/session-init-modal';
import StartScreen from '../start-screen/StartScreen';
import { Language, RecordingMode, RequiredOnSessionInit } from '#/types/preferences';
import { useTranslation } from 'react-i18next';
import { useWebSocket, WebSocketReply } from '#/contexts/WebsocketContext';

import '#/App.css';
import { UserMessageType, AiMessageType, FeatureRecord } from '#/types/message';
import { useRecording } from '#/contexts/RecordingContext';
import Notification from '../notification/notification';
import { fetchPromptSuggestions, fetchResponseMetadata } from '#/api/images';
import { useAudioPlayback } from '#/contexts/PlaybackContext';
import { Response } from '#/api/prompt';
import { ChecklistState, useChecklist } from '../../contexts/ChecklistContext';
import { ScreenSize, useScreenSize } from '#/hooks/useScreenSize';
import { usePropagateScroll } from '#/hooks/usePropagateScroll';
import logger from "#/utils/logger";
import { BrandingProvider } from '#/branding/BrandingContext';

interface NotificationState {
  message: string;
  visible: boolean;
}

import welcomeEN from '#/assets/sounds/hello_en.mp3';
import welcomeDE from '#/assets/sounds/hello_de.mp3';
import welcomeFR from '#/assets/sounds/hello_fr.mp3';
import welcomeFI from '#/assets/sounds/hello_fi.mp3';

const WELCOME_BY_LANG: Partial<Record<Language, string>> = {
  [Language.EN]: welcomeEN,
  [Language.DE]: welcomeDE,
  [Language.FR]: welcomeFR,
  [Language.FI]: welcomeFI,
};

const DEFAULT_MACHINE_LANGUAGES = [Language.EN, Language.DE, Language.FR, Language.FI];

const AuthenticatedApp: React.FC = () => {
  // States
  const [audioOutputFileName, setAudioOutputFileName] = useState<string>("");
  const [userMessages, setUserMessages] = useState<UserMessageType[]>([]);
  const [aiMessages, setAiMessages] = useState<AiMessageType[]>([]);
  const [machineLanguages, setMachineLanguages] = useState<Language[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState<Language>(Language.EN);
  const [machines, setMachines] = useState<string[]>([]);
  const [isMuted, setIsMuted] = useState(false);
  const [exhibitionModeEnabled, setExhibitionModeEnabled] = useState(false);
  const [selectedMachine, setSelectedMachine] = useState<string>("");
  const [selectedFeatures, setSelectedFeatures] = useState<FeatureRecord>({});
  const [serialNumber, setSerialNumber] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [notification, setNotification] = useState<NotificationState>({ message: '', visible: false });
  const [noAccess, setNoAccess] = useState<NoAccessProps | boolean>(false);
  const [backendVersion, setBackendVersion] = useState<string>("");
  const [toBeAnalyzed, setToBeAnalyzed] = useState(false);
  const [audioConfidence, setAudioConfidence] = useState(0);
  const [welcomeComplete, setWelcomeComplete] = useState(false);
  const [goToStartScreen, setGoToStartScreen] = useState(false);
  const [required, setRequired] = useState<RequiredOnSessionInit>({
    serial_number: false,
    features: [],
  });
  const [editingFeature, setEditingFeature] = useState<Feature[] | null>(null);
  const [sessionInitDone, setSessionInitDone] = useState(false);
  const [requiredFeatureObjects, setRequiredFeatureObjects] = useState<Feature[]>([]);
  const [initSaving, setInitSaving] = useState(false);


  // Refs
  const textStreamCompletedRef = useRef<boolean>(false);
  const responseMessageRef = useRef<string>("");
  const sessionRequestSent = useRef(false);
  const sessionIdRef = useRef("");
  const currentRequestId = useRef<number>(0);
  const chatContainerRef = useRef<HTMLDivElement | null>(null);

  // Hooks
  const { i18n } = useTranslation();
  const { setWebsocketSession, setCallback } = useWebSocket();
  const { checklistState, setChecklistState } = useChecklist();
  const { recordingMode, setRecordingMode, isMicAlwaysOn, setIsMicAlwaysOn } = useRecording();
  const { pauseRecording, resumeRecording, cancelRecording } = useRecording();
  const { pausePlaying  } = useAudioPlayback();
  const [welcomeAudioUrl, setWelcomeAudioUrl] = useState<string | undefined>(undefined);

  const screenSize = useScreenSize();

  usePropagateScroll();

  // Get current time
  function getTime() {
    return new Date().toLocaleTimeString('en-GB', { hour12: false });
  }

  const showNotification = (message: string) => {
    setNotification({ message, visible: true });
  };

  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, visible: false }));
  };

  function handleAudioDetected() {
    if (recordingMode == RecordingMode.Automatic) {
      // Prompt detected, pause recording
      logger.info("Speech detected, pausing recording")
      pauseRecording();
    }
  }

  function handleAudioDetectionFailed() {
    if (recordingMode == RecordingMode.Automatic) {
      // Prompt detected, pause recording
      logger.info("Speech to text not successful - continuing recording")
      resumeRecording();
    }
  }

  function handleAudioPrompt(prompt: string) {
    logger.debug("Handling audio prompt: ", prompt.substring(0, 40));
    pauseRecording();
    setAudioOutputFileName("");
    handleSubmit(prompt, true);
  }
  const triggerAudioPlayback = () => {
  };

  function handleAudioError(error: number, text: string, audioFile: string) {
    if (error === 100) {
      showNotification(text);
      return;
    }

    setUserMessages([]);
    setAiMessages(prev => {
      const newMessages = [...prev];
      newMessages[newMessages.length - 1] = {
        text: text,
        timestamp: getTime(),
        metadata: null
      };
      return newMessages;
    });
    if (!isMuted) {
      // Is triggered when the example same server side error is received twice in a row
      if (audioOutputFileName == audioFile) {
        triggerAudioPlayback();
      } else {
        setAudioOutputFileName(audioFile);
      }
    }
  }

  const hasInitRequirements = useMemo(() => {
    return !!required.serial_number || (required.features?.length ?? 0) > 0;
  }, [required.serial_number, required.features]);

  const sortFeatures = (features: FeatureRecord): FeatureRecord => {
    return Object.entries(features)
      .sort(([, [nameA]], [, [nameB]]) => nameA.localeCompare(nameB))
      .reduce<FeatureRecord>((obj, [key, value]) => {
        obj[key] = value;
        return obj;
      }, {});
  };

  const requiredFeatureKeys = useMemo(() => {
    return (required.features ?? []).filter(Boolean);
  }, [required.features]);

  const missingFeatureKeys = useMemo(() => {
    if (requiredFeatureKeys.length === 0) return [];

    return requiredFeatureKeys.filter((key) => {
      const selectedValue = selectedFeatures[key]?.[1];
      return selectedValue === null || selectedValue === undefined || selectedValue === "";
    });
  }, [requiredFeatureKeys, selectedFeatures]);

  useEffect(() => {
    let cancelled = false;

    const loadRequiredFeatures = async () => {
      if (requiredFeatureKeys.length === 0) {
        setRequiredFeatureObjects([]);
        return;
      }

      try {
        const features = await Promise.all(requiredFeatureKeys.map((k) => getFeature(k)));
        if (cancelled) return;

        setRequiredFeatureObjects(features.filter(Boolean) as Feature[]);
      } catch (e) {
        logger.error("Failed to fetch required feature objects:", e);
        if (!cancelled) setRequiredFeatureObjects([]);
      }
    };

    loadRequiredFeatures();

    return () => {
      cancelled = true;
    };
  }, [requiredFeatureKeys.join("|")]); // important: stable dependency

  const handleRequiredDropdownSelect = useCallback(
    async (feature_name: string, option_name: string) => {
      try {
        setInitSaving(true);

        const updated = await sendFeatureSelections([{ feature_name, option_name }]);
        setSelectedFeatures(sortFeatures(updated as FeatureRecord));
      } catch (e) {
        logger.error("Failed to save required dropdown selection:", e);
        showNotification("Failed to save selection");
      } finally {
        setInitSaving(false);
      }
    },
    [sortFeatures]
  );

  const handleRequiredFeatureSelectionComplete = async (
    selections: { feature_name: string; option_name: string }[]
  ) => {
    try {
      const updated = await sendFeatureSelections(selections);
      setSelectedFeatures(sortFeatures(updated as FeatureRecord));
    } catch (e) {
      logger.error("Failed to save required features:", e);
    } finally {
      setEditingFeature(null);
    }
  };

  const handleInitContinue = async () => {
    try {
      // Persist serial number if required
      if (required.serial_number) {
        const serialAsNumber = serialNumber.trim() === "" ? null : Number(serialNumber);

        await updateSettings(
          selectedLanguage,
          isMuted,
          selectedMachine,
          recordingMode,
          isMicAlwaysOn,
          exhibitionModeEnabled,
          serialAsNumber
        );
      }
    } catch (e) {
      logger.error("Failed to save session init values:", e);
      return;
    }

    setSessionInitDone(true); // Close modal for this refresh/session
  };

  async function handleResponse(response: Response, _isAudioPrompt: boolean, requestId: number) {
    if (requestId !== currentRequestId.current) return;

    // First update with the initial response metadata
    setAiMessages(prev => {
      if (requestId !== currentRequestId.current) return prev;
      const newMessages = [...prev];
      newMessages[newMessages.length - 1] = {
        text: response.response_text,
        timestamp: getTime(),
        metadata: response.metadata
      };
      return newMessages;
    });

    // scroll once the main text is in place
    scrollAfterPaint();

    if (response.sound_file_name) {
      logger.debug("Setting audio output filename to ", response.sound_file_name);
      setAudioOutputFileName(response.sound_file_name);
    } else {
      void resumeRecording();
    }
    // Then fetch and update with any additional metadata
    if (!(response.metadata.features || response.metadata.checklists || response.telemetry_response)) {
      setToBeAnalyzed(true);
      fetchResponseMetadata(sessionIdRef.current).then(metadata => {
      if (requestId !== currentRequestId.current || !metadata) return;
        setAiMessages(prev => {
          if (requestId !== currentRequestId.current) return prev;
          const newMessages = [...prev];
          newMessages[newMessages.length - 1] = {
            ...newMessages[newMessages.length - 1],
            metadata: {
              ...newMessages[newMessages.length - 1].metadata,
              ...metadata
            }
          };
          setToBeAnalyzed(false);
          scrollAfterPaint();
          return newMessages;
        });
      }).catch(error => {
        logger.error('Error fetching metadata:', error);
      });
    } else {
      setToBeAnalyzed(false);
    }

    fetchPromptSuggestions(sessionIdRef.current).then(suggestions => {
      if (requestId !== currentRequestId.current || !suggestions) return;
      if (suggestions) {
        setAiMessages(prev => {
          if (requestId !== currentRequestId.current) return prev;
          const newMessages = [...prev];
          const lastIndex = newMessages.length - 1;
          const lastMessage = newMessages[lastIndex];

          const existingMetadata = lastMessage.metadata ?? {
            context_relevant: false,
            display_items: [],
            prompt_suggestions: [],
            features: [],
            checklists: [],
          };

          const updatedMetadata = {
            ...existingMetadata,
            prompt_suggestions: suggestions
          };

          newMessages[lastIndex] = {
            ...lastMessage,
            metadata: updatedMetadata
          };
          scrollAfterPaint();

          return newMessages;
        });
      }
    });
  }

  async function handleSubmit(promptText: string, isAudioPrompt: boolean) {
    // Stop any playing audio before handling the new prompt
    pausePlaying();
    await new Promise<void>(r => setTimeout(r, 100));
    // Pause recording if not yet paused
    pauseRecording();

    const newUserMessage = {
      text: promptText,
      timestamp: getTime()
    };
    setUserMessages(prev => [...prev, newUserMessage]);

    responseMessageRef.current = "";
    const emptyAiMessage = {
      text: "",
      timestamp: "",
      metadata: null
    };
    setAiMessages(prev => [...prev, emptyAiMessage]);

    scrollAfterPaint();

    logger.debug("Clearing audio output filename");
    setAudioOutputFileName("");
    textStreamCompletedRef.current = false;

    try {
      const requestId = ++currentRequestId.current;
      const result = await sendPrompt(sessionIdRef.current, promptText, selectedLanguage, isMuted);
      await handleResponse(result, isAudioPrompt, requestId);
    } catch (error) {
    }
  }

  const onWebSocketMessage = (message: any) => {
    switch (message.type) {
      case WebSocketReply.CONFIDENCE:
        setAudioConfidence(message.payload);
        break;
      case WebSocketReply.DETECT:
        handleAudioDetected();
        break;
      case WebSocketReply.UNRECOGNIZED:
        handleAudioDetectionFailed();
        break;
      case WebSocketReply.PROMPT:
        handleAudioPrompt(message.payload);
        break;
      case WebSocketReply.ERROR:
        handleAudioError(message.payload.error, message.payload.text, message.payload.audio_file);
        break;
      case WebSocketReply.FORCE_TO_START_SCREEN:
        (async () => {
          setWelcomeAudioUrl(undefined);
          cancelRecording();
          pausePlaying();
          setAudioOutputFileName("");
          setUserMessages([]);
          setAiMessages([]);
          sessionIdRef.current = "";

          await i18n.changeLanguage(Language.DE);
          setSelectedLanguage(Language.DE);

          setGoToStartScreen(true);

          try {
            // Request new session
            await fetch(`${config.backendUrl}/session`, {
              method: 'GET',
              headers: await getAuthHeader()
            });

            // Then refetch data
            await refetchSession(Language.DE);
            setChecklistState(ChecklistState.CLOSED);
          } catch (e) {
            console.error("Failed to reset session:", e);
          }
        })();
        break;
      default:
        logger.warn('Unknown message type received:', message.type);
    }
  };

  const refetchSession = async (languageOverride?: Language) => {
    try {
      const response = await fetch(`${config.backendUrl}/session`, {
        method: 'GET',
        headers: await getAuthHeader()
      });
      if (!response.ok) throw new Error("Session fetch failed");
      const responseData = await response.json();

      currentRequestId.current++;

      const langToUse = languageOverride ?? responseData.preferences.language;

      // Set preferences
      await i18n.changeLanguage(langToUse);
      setSelectedLanguage(langToUse);
      setIsMuted(responseData.preferences.muted);
      setMachines(responseData.machines);
      setSelectedMachine(responseData.selected_machine.name);
      setSerialNumber(
        responseData.selected_machine?.serial_number != null
          ? String(responseData.selected_machine.serial_number)
          : ""
      );

      const features = responseData.selected_machine.features || {};
      const typedFeatures = features as Record<string, [string, string | boolean | null]>;
      const sortedFeatures = Object.entries(typedFeatures)
        .sort(([, [a]], [, [b]]) => a.localeCompare(b))
        .reduce<FeatureRecord>((obj, [key, val]) => {
          obj[key] = val;
          return obj;
      }, {});
      setSelectedFeatures(sortedFeatures);

      setRecordingMode(responseData.preferences.recording_mode);
      setIsMicAlwaysOn(responseData.preferences.is_mic_always_on ?? false);
      const exhibition = responseData.preferences?.exhibition_mode ?? false;
      setExhibitionModeEnabled(exhibition);

      sessionIdRef.current = responseData.session_id;
      setBackendVersion(responseData.backend_version);

      // Set welcome message + metadata again in selected language
      setUserMessages([]);
      setAiMessages([{
        text: responseData.welcome.message,
        timestamp: getTime(),
        metadata: {
          context_relevant: true,
          display_items: responseData.welcome.display_items,
          prompt_suggestions: responseData.welcome.prompt_suggestions
        }
      }]);

      setWebsocketSession(sessionIdRef.current);
    } catch (e) {
      logger.error("Failed to refetch session:", e);
    }
  };

  useEffect(() => {
    scrollAfterPaint();
  }, [userMessages.length, aiMessages.length]);

  useEffect(() => {
    if (sessionRequestSent.current) return;

    const getUserSession = async () => {
      try {
        const response = await fetch(`${config.backendUrl}/session`, {
          method: 'GET',
          headers: await getAuthHeader()
        });
        if (response.status === 403) {
          const data = await response.json();
          if (data.detail.error_code === 'user_not_found') {
            setNoAccess({
              errorCode: data.detail.error_code,
              accessRequestStatus: data.detail.access_request,
              accessRequestTime: data.detail.access_request_time
            });
          }
          else if (data.detail.error_code === 'user_access_expired') {
            setNoAccess({
              errorCode: data.detail.error_code,
              expirationDate: data.detail.expiration_date,
              accessRequestStatus: data.detail.access_request,
              accessRequestTime: data.detail.access_request_time
            });
          }
          return;

        }
        if (!response.ok) {
          throw new Error('User settings request failed');
        }
        const responseData = await response.json();

        // Set preferences
        setIsMuted(responseData.preferences.muted);
        setMachines(responseData.machines);
        setSelectedMachine(responseData.selected_machine.name);
        const currentMachineLanguages = responseData.selected_machine.languages ?? DEFAULT_MACHINE_LANGUAGES;
        setMachineLanguages(currentMachineLanguages);
        if (currentMachineLanguages.includes(responseData.preferences.language)) {
          i18n.changeLanguage(responseData.preferences.language);
          setSelectedLanguage(responseData.preferences.language);
        }
        else {
          // If the current language is not in machine languages, default to English
          i18n.changeLanguage(Language.EN);
          setSelectedLanguage(Language.EN);
        }
        setSerialNumber(
          responseData.selected_machine?.serial_number != null
            ? String(responseData.selected_machine.serial_number)
            : ""
        );
        setRequired(responseData?.selected_machine?.required_on_session_initialization ?? {
          serial_number: false,
          features: [],
        });
        const features = responseData.selected_machine.features || {};
        const exhibition = responseData.preferences?.exhibition_mode ?? false;
        setExhibitionModeEnabled(exhibition);
        setWelcomeComplete(!exhibition);

        const sortedFeatures = Object.entries(features as FeatureRecord)
          .sort(([, [nameA]], [, [nameB]]) => nameA.localeCompare(nameB))
          .reduce<FeatureRecord>((obj, [key, value]) => {
            obj[key] = value;
            return obj;
          }, {});

        setSelectedFeatures(sortedFeatures);
        setRecordingMode(responseData.preferences.recording_mode);
        setIsMicAlwaysOn(responseData.preferences.is_mic_always_on ?? false);

        // Set session id
        sessionIdRef.current = responseData.session_id;
        setBackendVersion(responseData.backend_version);

        // Set welcome message and image
        setAiMessages([{
          text: responseData.welcome.message,
          timestamp: getTime(),
          metadata: {
            context_relevant: true,
            display_items: responseData.welcome.display_items,
            prompt_suggestions: responseData.welcome.prompt_suggestions
          }
        }]);

        setWebsocketSession(sessionIdRef.current);
      } catch (error) {
        logger.error('Error getting user settings:', error);
        throw error;
      }
    }

    const initializeApp = async () => {
      setIsLoading(true);
      let retryCount = 0;
      while (true) {
        try {
          await getUserSession();
          break;
        } catch (error) {
          retryCount++;
          if (retryCount > 0) {
            setNotification({ message: `Failing to connect to the server. Retrying ${retryCount}...`, visible: true });
          }
          await new Promise(resolve => setTimeout(resolve, 4000));
          logger.error('Error initializing app:', error);
        }
      }
      setIsLoading(false);
      setCallback(onWebSocketMessage);
    };

    initializeApp();
    sessionRequestSent.current = true;
  }, []);

  useEffect(() => {
    if (checklistState === ChecklistState.CLOSED || checklistState === ChecklistState.HIDDEN) {
      if (screenSize === ScreenSize.DESKTOP) {
        document.getElementById('container')?.classList.remove('shift-content');
      }
    } else {
      if (screenSize === ScreenSize.DESKTOP) {
        document.getElementById('container')?.classList.add('shift-content');
      }
    }
  }, [checklistState, screenSize]);

  // Callback needs to be reset on changes in order to update the closure on the callback
  useEffect(() => {
    setCallback(onWebSocketMessage);
  }, [selectedLanguage, isMuted, recordingMode, isMicAlwaysOn])

  const showInitModal = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return hasInitRequirements && !sessionInitDone && params.get("openSettings") !== "true";
  }, [hasInitRequirements, sessionInitDone]);

  const continueDisabled = useMemo(() => {
    const needsFeatures = missingFeatureKeys.length > 0;
    return needsFeatures || initSaving;
  }, [missingFeatureKeys, initSaving]);

  const handleFeatureSelect = async (selections: { feature_name: string, option_name: string }[]) => {
    try {
      // Re-send the last prompt with the feature selections
      await sendFeatureSelections(selections);
    } catch (error) {
      logger.error('Error sending feature:', error);
    }

    try {
      handleSubmit(
        userMessages[userMessages.length - 1].text,
        false,
      );
    } catch (error) {
      logger.error('Error sending prompt with features:', error);
    }
  };

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    const el = chatContainerRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
  }, []);

  const scrollAfterPaint = useCallback(() => {
    // double rAF to ensure layout + paint completed (handles images/fonts)
    requestAnimationFrame(() => requestAnimationFrame(() => scrollToBottom('smooth')));
  }, [scrollToBottom]);

  const onStartMenuHidden = useCallback(async (lang: Language) => {
    // Pause recording immediately
    logger.debug("Start screen hidden. Pausing microphone for welcome message.");
    pauseRecording();
    await i18n.changeLanguage(lang);
    setSelectedLanguage(lang);

    // Set the state to trigger the audio playback in the visualizer
    setWelcomeAudioUrl(`${WELCOME_BY_LANG[lang] ?? welcomeEN}?t=${Date.now()}`);

    // Update UI state
    setWelcomeComplete(true);
    setGoToStartScreen(false);
  }, [selectedLanguage, pauseRecording]);

   // Render logic
   return (
    <BrandingProvider selectedMachine={selectedMachine}>
      {/* State: Loading */}
      {isLoading && (
        <div>
          <LoadingSpinner />
          <Notification {...notification} onClose={handleCloseNotification} duration={2500} />
        </div>
      )}

      {/* State: StartScreen (always mounted, hidden via prop) */}
      <StartScreen
        onStart={ onStartMenuHidden }
        refetchSession={refetchSession}
        visible={!isLoading && !noAccess && (!welcomeComplete || goToStartScreen)}
      />

      {/* State: NoAccess */}
      {!isLoading && noAccess && (typeof noAccess === 'object' ? <NoAccess {...noAccess} /> : <NoAccess />)}

      {/* State: Main app */}
      {!isLoading && welcomeComplete && !goToStartScreen && !noAccess && (
        <>
          <SessionInitModal
            visible={showInitModal}
            serialRequired={!!required.serial_number}
            serialNumber={serialNumber}
            setSerialNumber={setSerialNumber}
            requiredFeatures={requiredFeatureObjects}
            selectedFeatureValues={Object.fromEntries(
              Object.entries(selectedFeatures).map(([key, [, value]]) => [key, value])
            )}
            onSelectFeature={handleRequiredDropdownSelect}
            onContinue={handleInitContinue}
            continueDisabled={continueDisabled}
            loading={initSaving}
          />

          {editingFeature && (
            <FeatureSelection
              features={editingFeature}
              onClose={() => setEditingFeature(null)}
              onFeatureSelect={handleRequiredFeatureSelectionComplete}
            />
          )}

          <div className="top-bar" data-mode={recordingMode}>
            <Logo />
            <TopBarCenter />
            <Notification {...notification} onClose={handleCloseNotification} />
            <Settings
              machineLanguages={machineLanguages}
              selectedLanguage={selectedLanguage}
              setSelectedLanguage={setSelectedLanguage}
              machines={machines}
              isMuted={isMuted}
              setIsMuted={setIsMuted}
              exhibitionModeEnabled={exhibitionModeEnabled}
              setExhibitionModeEnabled={setExhibitionModeEnabled}
              selectedMachine={selectedMachine}
              selectedFeatures={selectedFeatures}
              setSelectedFeatures={setSelectedFeatures}
              serialNumber={serialNumber}
              setSerialNumber={setSerialNumber}
              backendVersion={backendVersion}
            />
          </div>
          <div className="app-container" id="container">
            <div ref={chatContainerRef} className="chat-container" id="chat-container">
              <Messages
                userMessages={userMessages}
                aiMessages={aiMessages}
                onFeatureSelect={handleFeatureSelect}
                isAnalyzingMetadata={toBeAnalyzed}
                audioOutputFileName={audioOutputFileName}
                isMuted={isMuted}
                onSubmit={(text) => handleSubmit(text, false)}
                isExhibitionMode={exhibitionModeEnabled}
                welcomeAudioUrl={welcomeAudioUrl}
              />
            </div>
            <div className="bottom-bar">
              <PromptContainer
                onSubmit={(text) => handleSubmit(text, false)}
                audioConfidence={audioConfidence}
                isMuted={isMuted}
                audioOutputFileName={audioOutputFileName}
              />
            </div>
          </div>
        </>
      )}
    </BrandingProvider>
  );
};

export default AuthenticatedApp;
